// react
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGoBack } from 'hooks/useGoBack';

// components
import { CareCircleInvitePage } from '@karehero/llama/pwa/pages/CareCircleInvitePage/CareCircleInvitePage';

// hooks
import { useCareCircle } from 'hooks';
import { useGetAllRelationshipQuery } from 'store/api/relationship';

// store
import { dispatch } from 'store';
import { useInviteCareCircleMutation } from 'store/api/careCircle';
import { newToast } from 'store/slices/uiSlice';

// utils
import { reportInviteError } from 'utils/inviteError';

/**
 * CareCircleInvite is the view for inviting a new member to the care circle.
 */
export const CareCircleInvite = () => {
  // hooks
  const { careCircleId } = useCareCircle();
  const [inviteCareCircle] = useInviteCareCircleMutation();
  const navigate = useNavigate();
  const goBack = useGoBack();
  const { data: relationships } = useGetAllRelationshipQuery();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // memos
  const relationshipOptions = useMemo(
    () =>
      (relationships || []).map((relationship) => ({
        value: relationship.id,
        label: relationship.name,
      })),
    [relationships],
  );

  // methods
  const handleSubmit = useCallback(
    async (data: {
      firstName: string;
      email: string;
      careRecipientAccountRelationshipId: string;
    }) => {
      if (!careCircleId) return;
      setIsSubmitting(true);
      try {
        await inviteCareCircle({
          careCircleId,
          ...data,
        }).unwrap();
        dispatch(newToast({ title: 'Email invite sent', variant: 'success' }));
        goBack();
      } catch (e) {
        reportInviteError(e);
        setIsSubmitting(false);
      }
    },
    [careCircleId, inviteCareCircle, navigate],
  );

  return (
    <CareCircleInvitePage
      relationshipOptions={relationshipOptions}
      onSubmit={handleSubmit}
      onBack={goBack}
      isSubmitting={isSubmitting}
    />
  );
};

export default CareCircleInvite;
